<template>
  <div>
    <b-card header-tag="header">
      <template #header>
        <h4><i class="fas fa-clipboard" />&nbsp;Journal d'événements</h4>
      </template>
      <div>
        <b-container fluid class="px-0">
          <b-row class="mb-2">
            <b-col
              cols="9"
              class="d-flex justify-content-center align-items-center"
            >
              <b-form-datepicker
                class="mr-1"
                v-model="fromDate"
                reset-button
                locale="fr"
                placeholder="Date de début"
                label-reset-button="Réinitialiser"
                label-no-date-selected="Date de début"
              ></b-form-datepicker>
              <b-form-datepicker
                class="ml-1"
                v-model="toDate"
                reset-button
                locale="fr"
                placeholder="Date de fin"
                label-reset-button="Réinitialiser"
                label-no-date-selected="Date de fin"
              ></b-form-datepicker>
              <b-button class="ml-4 w-50" @click="setDatesCurrentMonth"
                >Mois en cours</b-button
              >
            </b-col>
            <b-col class="d-flex justify-content-end">
              <b-overlay
                :show="isLoadingDownloadCSV"
                spinner-variant="primary"
                opacity="1"
              >
                <b-button variant="outline-primary" @click="downloadCSV"
                  ><i class="fas fa-download"></i>&nbsp;{{
                    $i18n.t('authLog.csvExport')
                  }}
                </b-button>
              </b-overlay>
            </b-col>
          </b-row>
        </b-container>

        <CustomTable
          class="p-0 text-left"
          :items="alarmHistory"
          :fields="fields"
          :filterable="true"
          :selectable="true"
          :current-page="currentPage"
          :per-page="perPage"
          :total-rows="totalRows"
          :tableOptions="tableOptions"
          :pageOptions="[10, 25, 50, 100]"
          :rowClassFunction="rowClass"
          @sort-changed="onSortChanged"
          @filtered="onFilterChanged"
          @page-changed="onPageChanged"
          @per-page-changed="onPerPageChanged"
        >
          <template #empty>
            <h4 class="text-secondary text-center mt-1">
              Aucune alarmes presente
            </h4>
          </template>

          <template #cell(datetime)="data">
            {{ formatDate(data.item.datetime) }}
          </template>
          <template #cell(datetimeAcq)="data">
            {{ formatDate(data.item.datetimeAcq) }}
          </template>
        </CustomTable>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import CustomTable from '@/components/CustomTable.vue';
import { EVENT_NAME } from '@/types/Alarm.ts';
import { exportToCSV } from '@/helpers/csvHelper';
import { now } from 'lodash';

const ALARM_HISTORY_COLLECTION = 'alarm-history';
const ALARM_RECEIVED = '1';
const ALARM_SELECTED = '2';
// const ALARM_NOT_PROCESSED = '3';
const ALARM_PROCESSED = '4';
const ALARM_START_INTERVENTION = '5';
const ALARM_END_INTERVENTION = '6';
const DATE_FORMAT = 'yyyy-MM-DD HH:mm:ss';
const ALARM_DELAY_DANGER = 24 * 3600 * 1000;

export default {
  name: 'History',

  components: {
    CustomTable,
  },

  data() {
    return {
      isLoadingDownloadCSV: false,
      alarmHistory: [],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      roomId: null,
      filter: null,
      sort: null,
      fromDate: null,
      toDate: null,
      tableOptions: {
        'head-variant': 'light',
      },
      fields: [
        {
          key: 'alarmId',
          label: "N d'alarme",
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'datetime',
          label: 'Date/Heure de déclenchement',
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'sourceUserName',
          label: 'Utilisateur',
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'equipmentTypeName',
          label: 'Type de declencheur',
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'alarmReasonName',
          label: 'Cause',
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'positionName',
          label: 'Position',
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'handledByPersonnelName',
          label: 'Intervenant',
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'datetimeAcq',
          label: "Date/Heure de l'acquittement",
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'eventName',
          label: 'Statut',
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: '',
          label: 'Motif',
          sortable: true,
          thClass: 'bg-success text-light',
        },
      ],
    };
  },

  computed: {
    ...mapGetters('tenants', ['selectedTenant', 'selectedSoftTenant']),
  },

  async mounted() {
    await this.fetchAlarmHistory();
  },

  methods: {
    buildFetchQuery() {
      if (!this.selectedSoftTenant) {
        return {};
      }

      let query = {
        query: {
          bool: {
            must: [
              {
                match: {
                  systemId: this.selectedSoftTenant._source.reference,
                },
              },
            ],
          },
        },
      };

      if (this.fromDate || this.toDate) {
        query.query.bool.must.push({
          range: {
            datetime: {
              gt: this.fromDate
                ? moment(this.fromDate).format(DATE_FORMAT)
                : null,
              lt: this.toDate
                ? moment(this.toDate).add(1, 'days').format(DATE_FORMAT)
                : null, // Add 24h to reach the end of the day
            },
          },
        });
      }

      if (this.filter) {
        query.query.bool.must.push({
          multi_match: {
            query: this.filter,
            type: 'phrase_prefix',
            fields: ['*'],
          },
        });
      }

      if (this.sort) {
        const sorts = [];
        for (const v of this.sort) {
          for (const k of Object.keys(v)) {
            if (k === 'datetime' || k === 'datetimeAcq') {
              sorts.push({ [k]: v[k] });
            } else {
              sorts.push({ [`${k}.keyword`]: v[k] });
            }
          }
        }
        query.sort = sorts;
      } else {
        query.sort = {
          datetime: 'desc',
        };
      }

      return query;
    },
    async fetchAlarmHistory() {
      if (!this.selectedTenant) {
        this.showErrorToast('Aucune agence sélectionnée.');
        return;
      }
      if (!this.selectedSoftTenant) {
        this.showErrorToast('Aucune résidence sélectionnée.');
        return;
      }
      const query = this.buildFetchQuery();

      try {
        const res = await this.$kuzzle.query({
          controller: 'pvstudio',
          action: 'userAlarms',
          index: this.selectedTenant.index,
          collection: ALARM_HISTORY_COLLECTION,
          query,
          from: (this.currentPage - 1) * this.perPage,
          size: this.perPage,
        });

        const alarmHistory = res.result.hits.map(({ _id, _source }) => ({
          _id,
          ..._source,
          eventName: EVENT_NAME[_source.eventId]
            ? this.$i18n.t(`alarm.${EVENT_NAME[_source.eventId]}`)
            : _source.eventName,
        }));

        this.alarmHistory = alarmHistory;
        this.totalRows = res.result.total;
      } catch (error) {
        this.showErrorToast(
          "Désolé, une erreur est survenue lors de la récupération de l'historique des alarmes."
        );
        this.$log.error(error);
      }
    },
    async subscribe() {
      if (this.roomId) {
        await this.$kuzzle.realtime.unsubscribe(this.roomId);
        this.roomId = null;
      }

      this.roomId = await this.$kuzzle.realtime.subscribe(
        this.selectedTenant.index,
        ALARM_HISTORY_COLLECTION,
        {},
        async () => {
          await this.fetchAlarmHistory();
        }
      );
    },
    rowClass(item) {
      if (!item) return null;
      if (item.eventId === ALARM_PROCESSED) return null;

      if ((item.eventId === ALARM_RECEIVED ||
        item.eventId === ALARM_SELECTED ||
        item.eventId === ALARM_START_INTERVENTION ||
        item.eventId === ALARM_END_INTERVENTION)) {
          const dateMS = new Date(item.datetime).getTime();
          if (dateMS > (now() - ALARM_DELAY_DANGER)) {
            return 'font-weight-bold text-danger';
          }
      }

      return 'font-weight-bold';
    },
    async onPageChanged(page) {
      this.currentPage = page;
      await this.fetchAlarmHistory();
    },
    async onPerPageChanged(perPage) {
      this.perPage = perPage;
      this.currentPage = 1;
      await this.fetchAlarmHistory();
    },
    async onFilterChanged(filter) {
      this.filter = filter;
      await this.fetchAlarmHistory();
    },
    async onSortChanged(sort) {
      this.sort = [
        {
          [sort.sortBy]: {
            order: sort.sortDesc ? 'desc' : 'asc',
          },
        },
      ];
      await this.fetchAlarmHistory();
    },
    setDatesCurrentMonth() {
      const date = new Date();
      this.fromDate = new Date(date.getFullYear(), date.getMonth(), 1);
      this.toDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    },
    formatDate(dateString) {
      try {
        return this.$d(new Date(dateString), 'long');
      } catch {
        return 'N/A';
      }
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Erreur',
        variant: 'danger',
        toaster: 'b-toaster-bottom-right',
        appendToast: true,
        dismissible: true,
        noAutoHide: true,
      });
    },
    async downloadCSV() {
      this.isLoadingDownloadCSV = true;

      const size = 10000;
      let results = [];
      let from = 0;
      let total = 0;
      let hitsNumber = 0;

      // use search scroll API for more efficiency
      do {
        let res = await this.$kuzzle.query({
          controller: 'pvstudio',
          action: 'userAlarms',
          index: this.selectedTenant.index,
          collection: ALARM_HISTORY_COLLECTION,
          query: this.buildFetchQuery(),
          from: from,
          size: size,
        });

        results = results.concat(res.result.hits);
        hitsNumber = hitsNumber + size;
        from = from + size;

        total = res.result.total;
      } while (hitsNumber < total);

      const alarmHistory = results.map(({ _id, _source }) => ({
        _id,
        ..._source,
        eventName: EVENT_NAME[_source.eventId]
          ? this.$i18n.t(`alarm.${EVENT_NAME[_source.eventId]}`)
          : _source.eventName,
      }));

      const now = new Date(Date.now());
      const formattedDate = `${now.getFullYear()}_${
        now.getMonth() + 1
      }_${now.getDate()}-${now.getHours()}_${now.getMinutes()}`;
      const filename = `event-history-logs_${this.selectedTenant.longName}_${this.selectedSoftTenant._source.model}_${formattedDate}.csv`;

      const headersKey = [];
      const headersLabel = [];
      for (const field of this.fields) {
        headersKey.push(field.key);
        headersLabel.push(field.label);
      }

      const newAlarmHistory = alarmHistory.map((alarm) => {
        const filteredObj = {};

        headersKey.forEach((key) => {
          if (alarm.hasOwnProperty(key)) {
            const value = alarm[key];
            const formattedValue =
              value instanceof Date ? value.toLocaleString('fr') : value;
            filteredObj[key] = formattedValue;
          } else {
            filteredObj[key] = null;
          }
        });

        return filteredObj;
      });

      exportToCSV(filename, newAlarmHistory, headersLabel);
      this.isLoadingDownloadCSV = false;
    },
  },

  watch: {
    async selectedSoftTenant() {
      await this.fetchAlarmHistory();
    },
    async alarmHistory() {
      await this.subscribe();
    },
    async fromDate() {
      await this.fetchAlarmHistory();
    },
    async toDate() {
      await this.fetchAlarmHistory();
    },
  },
};
</script>

<style lang="scss" scoped>
.pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
.table {
  color: $primary;
}

.b-table-sticky-header {
  max-height: 60vh !important;
}
</style>
